import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Package, Calendar, Building2, FileText } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Badge, ConfirmDialog, Modal, TextArea, toast } from '../../components/common';
import { goodsReceiptApi } from '../../services/api';
import './GoodsReceiptDetail.css';

export default function GoodsReceiptDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [actionType, setActionType] = useState('');
    const [rejectReason, setRejectReason] = useState('');

    const { data: gr, isLoading } = useQuery({
        queryKey: ['goodsReceipt', id],
        queryFn: () => goodsReceiptApi.getById(id),
        select: (res) => res.data?.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ action, data }) => {
            switch (action) {
                case 'accept': return goodsReceiptApi.accept(id);
                case 'reject': return goodsReceiptApi.reject(id, data);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Goods ${action}ed successfully`);
            queryClient.invalidateQueries(['goodsReceipt', id]);
            setActionType('');
            setRejectReason('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const config = {
            'pending_inspection': 'warning',
            'inspected': 'info',
            'accepted': 'success',
            'rejected': 'danger'
        };
        return <Badge variant={config[status] || 'default'} size="lg">{status?.replace('_', ' ')}</Badge>;
    };

    if (isLoading) return <div className="loading-state">Loading...</div>;
    if (!gr) return <div className="error-state">Goods Receipt not found</div>;

    const lines = gr.lines || gr.items || [];

    const canAccept = gr.status === 'inspected';
    const canReject = gr.status === 'inspected';

    return (
        <div className="gr-detail-page">
            <PageHeader
                title={gr.grNumber}
                breadcrumbs={[
                    { label: 'Goods Receipts', path: '/goods-receipts' },
                    { label: gr.grNumber }
                ]}
                actions={
                    <div className="gr-actions">
                        {canAccept && (
                            <Button icon={CheckCircle} onClick={() => setActionType('accept')}>
                                Accept
                            </Button>
                        )}
                        {canReject && (
                            <Button variant="danger" icon={XCircle} onClick={() => setActionType('reject')}>
                                Reject
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="gr-detail-content">
                <Card>
                    <CardBody>
                        <div className="gr-header">
                            <div>
                                <h2>{gr.grNumber}</h2>
                            </div>
                            {getStatusBadge(gr.status)}
                        </div>

                        <div className="gr-meta-grid">
                            <div className="meta-item">
                                <FileText size={16} />
                                <span className="meta-label">PO Number</span>
                                <span className="meta-value">{gr.purchaseOrder?.poNumber}</span>
                            </div>
                            <div className="meta-item">
                                <Building2 size={16} />
                                <span className="meta-label">Vendor</span>
                                <span className="meta-value">{gr.purchaseOrder?.vendor?.name}</span>
                            </div>
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span className="meta-label">Received Date</span>
                                <span className="meta-value">{formatDate(gr.receivedDate)}</span>
                            </div>
                            <div className="meta-item">
                                <Package size={16} />
                                <span className="meta-label">Items</span>
                                <span className="meta-value">{lines.length} items</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h3>Received Items</h3>
                        <table className="gr-items-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Ordered</th>
                                    <th>Received</th>
                                    <th>Accepted</th>
                                    <th>Rejected</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="item-info">
                                                <span className="item-code">{item.itemCode || item.catalogItem?.itemCode}</span>
                                                <span className="item-name">{item.itemName || item.catalogItem?.name}</span>
                                            </div>
                                        </td>
                                        <td>{item.orderedQuantity} {item.uomCode || item.catalogItem?.unitOfMeasure?.code || 'PCS'}</td>
                                        <td>{item.receivedQuantity}</td>
                                        <td className="qty-accepted">{item.acceptedQuantity || 0}</td>
                                        <td className="qty-rejected">{item.rejectedQuantity || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardBody>
                </Card>

                {gr.notes && (
                    <Card>
                        <CardBody>
                            <h3>Notes</h3>
                            <p>{gr.notes}</p>
                        </CardBody>
                    </Card>
                )}
            </div>

            <ConfirmDialog
                isOpen={actionType === 'accept'}
                onClose={() => setActionType('')}
                onConfirm={() => actionMutation.mutate({ action: 'accept' })}
                title="Accept Goods"
                message="Are you sure you want to accept this goods receipt?"
                confirmText="Accept"
                loading={actionMutation.isPending}
            />

            <Modal
                isOpen={actionType === 'reject'}
                onClose={() => { setActionType(''); setRejectReason(''); }}
                title="Reject Goods"
                size="sm"
            >
                <div className="reject-form">
                    <p>Please provide a reason for rejection.</p>
                    <TextArea
                        label="Reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={3}
                        required
                    />
                    <div className="form-actions">
                        <Button variant="secondary" onClick={() => { setActionType(''); setRejectReason(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => actionMutation.mutate({ action: 'reject', data: { reason: rejectReason } })}
                            loading={actionMutation.isPending}
                            disabled={!rejectReason.trim()}
                        >
                            Reject
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
